import type { DeviceState, DeviceWalkInMenu, DeviceWalkInQuote, DeviceWalkInView, PaymentSession } from "@vaulted/shared";
import { formatCents } from "@vaulted/shared";
import { useEffect, useState } from "react";
import { StripeCheckout } from "../components/StripeCheckout";
import { Alert, Button, DateOfBirthField, Field } from "../components/ui";
import { Frame } from "./Forms";

/** Requests as this iPad (its own token). */
export type DeviceCall = <T>(method: string, path: string, body?: unknown) => Promise<T>;

const message = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong.");

/**
 * A walk-in on the iPad, one person: pick a service, see the first open time, give their details,
 * then (card on file kept) save a card. Once it's on file the visit is checked in and their forms open.
 */
export type WalkInStart = { returning: { clientId: string; firstName: string; lastName: string; dob: string } | null; prefill: { lastName: string; dob: string } | null };

export function WalkIn({ api, state, start, onCancel, onBooked, footer }: { api: DeviceCall; state: DeviceState; start: WalkInStart; onCancel: () => void; onBooked: () => void; footer: React.ReactNode }) {
  const returning = start.returning;
  const [menu, setMenu] = useState<DeviceWalkInMenu | null>(null);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [addOnIds, setAddOnIds] = useState<string[]>([]);
  // An event's recovery option (Normatec): with the drip, or on its own.
  const [extraId, setExtraId] = useState<string | null>(null);
  const [quote, setQuote] = useState<DeviceWalkInQuote | null>(null);
  const [person, setPerson] = useState({ firstName: "", lastName: start.prefill?.lastName ?? "", dob: start.prefill?.dob ?? "", mobile: "", email: "" });
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useIdle(3 * 60_000, onCancel);

  useEffect(() => {
    api<DeviceWalkInMenu>("GET", "/api/device/walk-in/menu").then(setMenu, (e) => setError(message(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  // An event's shots are services of their own on the visit, beside the drip.
  const isShot = (id: string) => !!menu?.addOns.find((a) => a.id === id)?.service;
  const visit = (drip: string | null, addOns: string[], extra: string | null) => [
    ...(drip ? [{ serviceId: drip, addOnIds: addOns.filter((id) => !isShot(id)) }] : []),
    ...(drip ? addOns.filter(isShot).map((id) => ({ serviceId: id, addOnIds: [] })) : []),
    ...(extra ? [{ serviceId: extra, addOnIds: [] }] : []),
  ];
  const services = visit(serviceId, addOnIds, extraId);
  const findTime = (drip: string | null, addOns: string[], extra: string | null) => run(async () => setQuote(await api<DeviceWalkInQuote>("POST", "/api/device/walk-in/quote", { services: visit(drip, addOns, extra) })));
  const book = () =>
    run(async () => {
      try {
        await api("POST", "/api/device/walk-in", {
          services,
          start: quote!.start,
          agreed,
          ...(returning ? { returning: { clientId: returning.clientId, lastName: returning.lastName, dob: returning.dob } } : { person }),
        });
        onBooked();
      } catch (e) {
        // The time went while they typed: show the next one.
        if ((e as { status?: number }).status === 409) setQuote(await api<DeviceWalkInQuote>("POST", "/api/device/walk-in/quote", { services }));
        throw e;
      }
    });

  const service = menu?.services.find((s) => s.id === serviceId);
  const drips = menu?.services.filter((s) => s.role === "drip") ?? [];
  const extras = menu?.services.filter((s) => s.role === "extra") ?? [];
  const extra = extras.find((s) => s.id === extraId);
  // A drip's next screen: its add-ons and the recovery options (straight to the time when there are none).
  const hasChoices = (s: { addOnIds: string[] }) => s.addOnIds.length > 0 || extras.length > 0;
  const choice = (s: (typeof drips)[number], onClick: () => void) => (
    <li key={s.id}>
      <button type="button" disabled={busy} className="w-full rounded-[var(--radius-control)] border border-line bg-white px-4 py-3 text-left active:border-brand active:bg-brand/5" onClick={onClick}>
        <span className="flex justify-between gap-3 font-semibold">
          <span>{s.name}</span>
          <span className="tabular-nums">{formatCents(s.priceCents)}</span>
        </span>
        {s.description && <span className="mt-0.5 block text-sm text-ink-soft">{s.description}</span>}
      </button>
    </li>
  );
  const tile = "flex items-center gap-3 rounded-[var(--radius-control)] border border-line bg-white px-3 py-2.5 has-checked:border-brand has-checked:bg-brand/5";
  // Each step starts at the top of the screen.
  useEffect(() => window.scrollTo(0, 0), [serviceId, extraId, quote]);
  const set = (k: keyof typeof person) => (e: React.ChangeEvent<HTMLInputElement>) => setPerson({ ...person, [k]: e.target.value });
  const back = (
    <Button type="button" variant="quiet" onClick={onCancel}>
      Cancel
    </Button>
  );

  return (
    <Frame clinic={state.clinicName} onStartOver={onCancel}>
      <div className={`mx-auto flex flex-col gap-4 ${service && !quote ? "max-w-2xl py-2" : "max-w-lg py-8"}`}>
        {!menu ? (
          error ? (
            <>
              <Alert>{error}</Alert>
              {back}
            </>
          ) : (
            <p className="py-16 text-center text-muted">Loading the menu…</p>
          )
        ) : !quote && !service && !extra ? (
          <>
            <h1 className="font-display text-3xl">{returning ? `Welcome back, ${returning.firstName}` : "Walk in"}</h1>
            <p className="text-ink-soft">What would you like today?</p>
            <ul className="flex flex-col gap-2">{drips.map((s) => choice(s, () => (setServiceId(s.id), setAddOnIds([]), setExtraId(null), hasChoices(s) ? undefined : void findTime(s.id, [], null))))}</ul>
            {extras.length > 0 && (
              <>
                <h2 className="mt-2 font-display text-xl">{menu.extrasTitle}</h2>
                {drips.length > 0 && <p className="-mt-2 text-sm text-ink-soft">On its own, or pick a drip above and add it there.</p>}
                <ul className="flex flex-col gap-2">{extras.map((s) => choice(s, () => (setServiceId(null), setAddOnIds([]), setExtraId(s.id), void findTime(null, [], s.id))))}</ul>
              </>
            )}
            {error && <Alert>{error}</Alert>}
            {back}
          </>
        ) : !quote && service ? (
          <>
            {/* Fits an iPad screen: two columns of tiles, the buttons always in view. */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-4">
              <h1 className="font-display text-2xl">{hasChoices(service) ? "Add anything?" : service.name}</h1>
              <p className="text-ink-soft">
                {service.name} · {formatCents(service.priceCents)}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {service.addOnIds.map((id) => {
                const a = menu.addOns.find((x) => x.id === id);
                if (!a) return null;
                return (
                  <label key={id} className={tile}>
                    <input type="checkbox" className="size-5 shrink-0" checked={addOnIds.includes(id)} onChange={(e) => setAddOnIds(e.target.checked ? [...addOnIds, id] : addOnIds.filter((x) => x !== id))} />
                    <span className="flex-1 leading-tight">{a.name}</span>
                    <span className="tabular-nums text-ink-soft">+{formatCents(a.priceCents)}</span>
                  </label>
                );
              })}
            </div>
            {extras.length > 0 && (
              <>
                <h2 className="font-display text-xl">{menu.extrasTitle}</h2>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {extras.map((x) => (
                    <label key={x.id} className={tile}>
                      {/* One at most: ticking another swaps it. */}
                      <input type="checkbox" className="size-5 shrink-0" checked={extraId === x.id} onChange={(e) => setExtraId(e.target.checked ? x.id : null)} />
                      <span className="flex-1 leading-tight">{x.name}</span>
                      <span className="tabular-nums text-ink-soft">+{formatCents(x.priceCents)}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            {error && <Alert>{error}</Alert>}
            <div className="sticky bottom-0 -mx-4 flex gap-3 border-t border-line bg-paper/95 px-4 py-3 backdrop-blur">
              <Button variant="primary" className="flex-1 py-3 text-lg" busy={busy} onClick={() => void findTime(service.id, addOnIds, extraId)}>
                {addOnIds.length || extraId ? "Find my time" : "Nothing else: find my time"}
              </Button>
              <Button variant="quiet" onClick={() => (setServiceId(null), setExtraId(null), setError(null))}>
                Back
              </Button>
            </div>
          </>
        ) : !quote ? (
          // A recovery option on its own: its time is being found (or couldn't be).
          <>
            {error ? <Alert>{error}</Alert> : <p className="py-16 text-center text-muted">Finding your time…</p>}
            <Button variant="quiet" onClick={() => (setExtraId(null), setError(null))}>
              Back
            </Button>
          </>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void book();
            }}
          >
            <div className="rounded-[var(--radius-panel)] bg-mist px-4 py-3">
              <p className="text-lg">
                Your time today: <b>{quote!.time}</b>
              </p>
              <p className="text-sm text-ink-soft">
                {[service?.name, extra?.name].filter(Boolean).join(" + ")}
                {addOnIds.length ? ` + ${addOnIds.length} add-on${addOnIds.length > 1 ? "s" : ""}` : ""} · {formatCents(quote!.totalCents)}
              </p>
            </div>
            {!returning && (
              <>
                <h1 className="font-display text-2xl">About you</h1>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First name" autoComplete="off" autoCapitalize="words" value={person.firstName} onChange={set("firstName")} />
                  <Field label="Last name" autoComplete="off" autoCapitalize="words" value={person.lastName} onChange={set("lastName")} />
                </div>
                <DateOfBirthField value={person.dob} onChange={(dob) => setPerson({ ...person, dob })} />
                <Field label="Mobile number" type="tel" inputMode="tel" autoComplete="off" value={person.mobile} onChange={set("mobile")} />
                <Field label="Email" type="email" inputMode="email" autoComplete="off" autoCapitalize="none" value={person.email} onChange={set("email")} />
              </>
            )}
            {menu.agreement && (
              <label className="flex items-start gap-3 rounded-[var(--radius-control)] border border-line bg-white px-4 py-3 text-sm">
                <input type="checkbox" className="mt-0.5 size-5" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                <span>{menu.agreement}</span>
              </label>
            )}
            {error && <Alert>{error}</Alert>}
            <div className="flex gap-3">
              <Button
                type="submit"
                variant="primary"
                className="flex-1 py-3 text-lg"
                busy={busy}
                disabled={(!returning && (!person.firstName.trim() || !person.lastName.trim() || !person.dob || !person.mobile.trim() || !person.email.trim())) || (!!menu.agreement && !agreed)}
              >
                {menu.agreement ? "Next: card on file" : "Check me in"}
              </Button>
              <Button type="button" variant="quiet" onClick={() => (setQuote(null), setError(null), service && hasChoices(service) ? undefined : (setServiceId(null), setExtraId(null)))}>
                Back
              </Button>
            </div>
          </form>
        )}
      </div>
      {footer}
    </Frame>
  );
}

/** The walk-in's card on file: the one already saved, the card reader, or typing it in. */
export function WalkInCard({
  api,
  state,
  walkIn,
  onCancel,
  onFrontDesk,
  footer,
}: {
  api: DeviceCall;
  state: DeviceState;
  walkIn: DeviceWalkInView;
  onCancel: () => void;
  onFrontDesk: () => void;
  footer: React.ReactNode;
}) {
  const [session, setSession] = useState<PaymentSession | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useIdle(5 * 60_000, onCancel);

  const pick = async (method: "on_file" | "reader" | "form") => {
    setBusy(method);
    setError(null);
    try {
      const r = await api<{ session: PaymentSession | null }>("POST", "/api/device/walk-in/card", { method });
      if (r.session) setSession(r.session);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Frame clinic={state.clinicName} onStartOver={onCancel}>
      <div className="mx-auto flex max-w-lg flex-col gap-4 py-8">
        <h1 className="font-display text-3xl">Thanks, {walkIn.firstName}!</h1>
        <p className="text-ink-soft">
          {walkIn.serviceName} at {walkIn.time}. Last step: a card on file. Nothing is charged now; you pay after your visit.
        </p>
        {saved ? (
          <p className="py-6 text-center text-lg">Saving your card…</p>
        ) : session ? (
          <div className="flex flex-col gap-2">
            <StripeCheckout session={session} onComplete={() => (setSession(null), setSaved(true))} />
            <Button variant="quiet" onClick={() => setSession(null)}>
              Back
            </Button>
          </div>
        ) : walkIn.waitingOnReader ? (
          <div className="flex flex-col items-center gap-2 rounded-[var(--radius-panel)] bg-mist px-4 py-6 text-center">
            <p className="text-lg font-semibold">Tap or insert your card on the reader{walkIn.reader ? ` (${walkIn.reader.label})` : ""}.</p>
            <p className="text-sm text-ink-soft">It's saved, not charged. This screen updates by itself.</p>
            <Button variant="quiet" busy={busy === "stop"} onClick={() => (setBusy("stop"), void api("POST", "/api/device/walk-in/stop-reader").catch((e) => setError(message(e))).finally(() => setBusy(null)))}>
              Use another way
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {walkIn.readerError && <Alert>The card wasn't saved: {walkIn.readerError} Try again, or another way.</Alert>}
            {walkIn.onFile && (
              <Button variant="primary" className="py-4 text-lg" busy={busy === "on_file"} onClick={() => void pick("on_file")}>
                Use my {walkIn.onFile.brand.toUpperCase()} •••• {walkIn.onFile.last4} on file
              </Button>
            )}
            {walkIn.reader && (
              <Button variant={walkIn.onFile ? "secondary" : "primary"} className="py-4 text-lg" busy={busy === "reader"} onClick={() => void pick("reader")}>
                Tap or insert my card on the reader
              </Button>
            )}
            <Button variant={walkIn.onFile || walkIn.reader ? "secondary" : "primary"} className="py-4 text-lg" busy={busy === "form"} onClick={() => void pick("form")}>
              Type my card
            </Button>
          </div>
        )}
        {error && <Alert>{error}</Alert>}
        <Button variant="quiet" onClick={onFrontDesk}>
          I'll give my card to the front desk
        </Button>
      </div>
      {footer}
    </Frame>
  );
}

/** Back to the welcome screen after this long without a tap (someone walked away mid-way). */
export function useIdle(ms: number, onIdle: () => void) {
  useEffect(() => {
    let t = setTimeout(onIdle, ms);
    const reset = () => {
      clearTimeout(t);
      t = setTimeout(onIdle, ms);
    };
    const events = ["pointerdown", "keydown", "input"];
    for (const e of events) window.addEventListener(e, reset);
    return () => {
      clearTimeout(t);
      for (const e of events) window.removeEventListener(e, reset);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms]);
}
